import { Box, Typography, Stack, TextField, Paper, Divider } from "@mui/material";
import Header from "../../../components/Header";
import TableAgents from "./components/TableAgents";
import API from "../../../api";
import { useEffect, useMemo, useState } from "react";
import CircleIcon from "@mui/icons-material/Circle";

const Agent = () => {
  const [, setSelectedLicense] = useState("");
  const [online, setOnline] = useState([]);
  const [offline, setOffline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const license = sessionStorage.getItem("License");
    if (license) setSelectedLicense(license);
  }, []);

  useEffect(() => {
    setLoading(true);

    API.getMachines()
      .then((res) => {
        
        setOnline(res.data.online || []);
        setOffline(res.data.offline || []);
      })
      .catch(() => {
        setOnline([]);
        setOffline([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const onlineCount = useMemo(() => online?.length ?? 0, [online]);
  const offlineCount = useMemo(() => offline?.length ?? 0, [offline]);

  const visibleOnlineCount = useMemo(() => {
    if (!search) return online?.length ?? 0;

    const regex = new RegExp(search, "i");

    return (online || []).filter(
      (agent) =>
        regex.test(agent.machine_name || "") ||
        regex.test(agent.ip || "") ||
        regex.test(agent.mac || "")
    ).length;
  }, [online, search]);

  const visibleOfflineCount = useMemo(() => {
    if (!search) return offline?.length ?? 0;

    const regex = new RegExp(search, "i");

    return (offline || []).filter(
      (agent) =>
        regex.test(agent.machine_name || "") ||
        regex.test(agent.ip || "") ||
        regex.test(agent.mac || "")
    ).length;
  }, [offline, search]);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
      }}
    >
      <Header title={"Agent Monitor"} />

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 2,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          backgroundImage:
            "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0))",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography sx={{ fontSize: 14, opacity: 0.8 }}>
              Search agent
            </Typography>

            <TextField
              size="small"
              variant="outlined"
              placeholder="Type a name, host, id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 360 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  backgroundColor: "background.paper",
                },
                "& fieldset": { borderColor: "divider" },
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            sx={{ flexWrap: "wrap" }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircleIcon sx={{ fontSize: 10, color: "success.main" }} />
              <Typography sx={{ fontSize: 13, opacity: 0.9 }}>
                Online
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {onlineCount}
              </Typography>
            </Box>

            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircleIcon sx={{ fontSize: 10, color: "error.main" }} />
              <Typography sx={{ fontSize: 13, opacity: 0.9 }}>
                Offline
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {offlineCount}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderLeft: "4px solid",
              borderLeftColor: "success.main",
              backgroundImage:
                "linear-gradient(90deg, rgba(46,125,50,0.12), rgba(0,0,0,0))",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CircleIcon sx={{ color: "success.main", fontSize: 12 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                Online
              </Typography>
            </Stack>

            <Typography sx={{ fontSize: 13, opacity: 0.8 }}>
              {loading ? "Loading..." : `${visibleOnlineCount} agent`}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            <TableAgents
              data={online}
              search={search}
              loading={loading}
              tableHeight={{ xs: 420, md: 520, lg: 600 }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderLeft: "4px solid",
              borderLeftColor: "error.main",
              backgroundImage:
                "linear-gradient(90deg, rgba(211,47,47,0.12), rgba(0,0,0,0))",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CircleIcon sx={{ color: "error.main", fontSize: 12 }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                Offline
              </Typography>
            </Stack>

            <Typography sx={{ fontSize: 13, opacity: 0.8 }}>
              {loading ? "Loading..." : `${visibleOfflineCount} agent`}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            <TableAgents
              data={offline}
              search={search}
              loading={loading}
              tableHeight={{ xs: 420, md: 520, lg: 600 }}
            />
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export default Agent;